import type { ChangeEvent } from 'react';
import { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import {
  Accordion,
  AccordionDetails,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@/shared/ui';

import { Z_INDEX } from '@/config/z-index';
import type { ExtendedSeriesOptionsType } from '@/features/_shared/chart/types/highcharts';
import { useUserConfig } from '@/features/_shared/user';
import AbsoluteSelector from '@/features/markets/components/Chart/ForwardCurveChartBlock/CurveComparisonSelector/AbsoluteSelector';
import { parseSelectedToIso } from '@/features/markets/components/Chart/ForwardCurveChartBlock/CurveComparisonSelector/parseRelativeDateSelectionToIso';
import RelativeSelector from '@/features/markets/components/Chart/ForwardCurveChartBlock/CurveComparisonSelector/RelativeSelector';
import Popper from '@/shared/ui/overlays/Popover';
import Button from '@/shared/ui/primitives/Button';

import { getLatestTransactionDate } from './util/getLatestTransactionDate';

const selectedDateRowSx = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  width: 170
};
const labelRowSx = { alignItems: 'center', display: 'flex', width: 170 };
const dropdownSx = {
  minWidth: 400,
  borderBottom: '1px solid',
  borderLeft: '1px solid',
  borderColor: 'gx.highlight',
  display: 'flex',
  flexDirection: 'column',
  fontSize: '14px'
};
const detailsSx = {
  backgroundColor: 'gx.tileLight',
  display: 'flex',
  flexDirection: 'column'
};
const dateTitleSx = { mb: 1.5, fontSize: 16, fontWeight: 700 };
const dateSubTitleSx = { mb: 1.5 };
const addBtnSx = { color: 'gx.highlight', borderColor: 'gx.highlight', mx: 3, mt: 1 };

const showDuplicatedErrorMessage = () => {
  enqueueSnackbar('This date is already selected!', {
    variant: 'error',
    autoHideDuration: 5000
  });
};

const CurveComparisonSelector = ({
  chartSeries,
  label,
  transactionDates,
  comparisonSelectorIndex,
  deleteFromTemplateSeriesWith,
  populateTemplateSeriesWith
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  label: string;
  transactionDates: string[];
  comparisonSelectorIndex: number;
  deleteFromTemplateSeriesWith: (comparisonSelectorIndex: number) => void;
  populateTemplateSeriesWith: (selectedDateString: string, comparisonSelectorIndex: number) => void;
}) => {
  const [compareTo, setCompareTo] = useState<string>('1');
  const [compareToUnit, setCompareToUnit] = useState<string>('d');
  const latestTransactionDate: number | undefined = getLatestTransactionDate(chartSeries);

  const transactionDate =
    transactionDates.length > 0 ? transactionDates[comparisonSelectorIndex] : null;
  const [comparisonDate, setComparisonDate] = useState<Date | null>(
    transactionDate ? dayjs(transactionDate).toDate() : null
  );
  const [comparisonType, setComparisonType] = useState<string>(
    comparisonDate ? 'absolute' : 'relative'
  );
  const { data: userConfigData } = useUserConfig();
  const dateFormat = (userConfigData?.dateFormat.value ?? 'dd-MMM-yy').toUpperCase();

  const handleChangeRadio = (e: ChangeEvent<HTMLInputElement>) => {
    setComparisonType(e.target.value);
  };
  const onAddComparison = () => {
    const selectedDateString = parseSelectedToIso({
      startPoint: latestTransactionDate,
      comparisonDate,
      comparisonType,
      compareTo,
      compareToUnit
    });
    if (transactionDates.includes(selectedDateString)) {
      showDuplicatedErrorMessage();
      return;
    }

    populateTemplateSeriesWith(selectedDateString, comparisonSelectorIndex);
  };

  return (
    <Box>
      <Popper
        buttonContent={
          <Box>
            {transactionDate ? (
              <Box sx={selectedDateRowSx}>
                <Typography variant='body2'>{dayjs(transactionDate).format(dateFormat)}</Typography>
                <CloseIcon
                  sx={{ fontSize: 16, ml: 1, mr: 0 }}
                  onClick={e => {
                    e.stopPropagation();
                    deleteFromTemplateSeriesWith(comparisonSelectorIndex);
                  }}
                />
              </Box>
            ) : (
              <Box sx={labelRowSx}>
                <Typography variant='body2'>{`${label} ${comparisonSelectorIndex + 1}`}</Typography>
                <ExpandMore sx={{ fontSize: 16, ml: 1, mr: 0 }} />
              </Box>
            )}
          </Box>
        }
        buttonVariant='outlined'
        modifiers={[
          {
            name: 'flip',
            enabled: false
          }
        ]}
        placement='bottom-start'
        sx={{ ml: 2 }}
        zIndex={Z_INDEX.panelFloating}
      >
        <Box sx={dropdownSx}>
          <Accordion defaultExpanded>
            <AccordionDetails sx={detailsSx}>
              <Typography sx={dateTitleSx}>Date selection</Typography>
              <Typography sx={dateSubTitleSx}>Select a date to compare</Typography>
              <FormControl>
                <RadioGroup
                  value={comparisonType}
                  onChange={handleChangeRadio}
                >
                  <RelativeSelector
                    compareTo={compareTo}
                    compareToUnit={compareToUnit}
                    setCompareTo={setCompareTo}
                    setCompareToUnit={setCompareToUnit}
                    setComparisonType={setComparisonType}
                  />

                  <FormControlLabel
                    control={<Radio />}
                    label='Compare to date'
                    value='absolute'
                  />
                  <AbsoluteSelector
                    comparisonDate={comparisonDate}
                    comparisonType={comparisonType}
                    setComparisonDate={setComparisonDate}
                    startPoint={latestTransactionDate}
                  />
                </RadioGroup>
              </FormControl>
              <Button
                sx={addBtnSx}
                variant='outlined'
                onClick={onAddComparison}
              >
                Add
              </Button>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Popper>
    </Box>
  );
};
export default CurveComparisonSelector;
